// src/components/chat/MessageList.js
import React from 'react';
import { Copy, FileText, Loader2, Volume2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useChat } from '../../hooks/useChat';
import ChatResponseDisplay from '../ChatResponseDisplay';
import WelcomeScreen from '../WelcomeScreen';

const ScoreBadge = ({ source, theme }) => {
  const rawScore = source?.relevance_score ?? source?.score ?? source?.rerank_score;
  if (rawScore == null || Number.isNaN(Number(rawScore))) return null;

  const numericScore = Number(rawScore);
  const displayValue = numericScore >= 0 && numericScore <= 1 
    ? `${(numericScore * 100).toFixed(1)}%` 
    : numericScore.toFixed(1);

  const badgeTone = theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700';

  return (
    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${badgeTone}`}>
      Score {displayValue}
    </span>
  );
};

const FilePathButton = ({ source, openDocumentModalWith }) => {
  const path = source.source_path || source.filename || 'N/A';
  if (!path || path === 'N/A') return <span>N/A</span>;

  const lastPart = path.split("/").pop();
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    lastPart && openDocumentModalWith(lastPart);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="font-semibold text-blue-600 dark:text-blue-400 break-all cursor-pointer hover:underline"
    >
      {lastPart}
      {source.slide_number && <span> - Slide {source.slide_number}</span>}
      {source.sheet_name && <span> - {source.sheet_name}</span>}
      {source.page_number && <span> - Page {source.page_number}</span>}
    </button>
  );
};

const MessageActions = ({ msg, index, theme, copyToClipboard, copySuccess, handleReadAloud, playingMessageId, showSourcesForMessage, setShowSourcesForMessage }) => (
  <div className={`flex items-center space-x-4 mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
    <button onClick={() => copyToClipboard(msg.content)} className={`${theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}`}>
      {copySuccess ? 'Copied!' : <Copy size={18} />}
    </button>

    <button
      onClick={() => handleReadAloud(msg.content, index)}
      disabled={playingMessageId !== null}
      className={`transition-colors ${theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'} ${playingMessageId === index ? 'text-blue-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
      title="Read Aloud"
    >
      <Volume2 size={18} />
    </button>

    {msg.sources?.length > 0 && (
      <button
        onClick={() => setShowSourcesForMessage(showSourcesForMessage === index ? null : index)}
        className={`${theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'} ${showSourcesForMessage === index ? 'text-blue-500' : ''}`}
      >
        <FileText size={18} />
      </button>
    )}

    {msg.responseTime && (
      <span className={`text-xs flex items-center gap-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} title="Response time">
        ⏱️ {(msg.responseTime / 1000).toFixed(1)}s
      </span>
    )}
  </div>
);

const MessageSources = ({ msg, theme, showSourcesForMessage, index, openDocumentModalWith }) => {
  if (showSourcesForMessage !== index || !msg.sources?.length) return null;

  return (
    <div className={`mt-2 p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
      <ul className={`list-decimal pl-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        {msg.sources.map((source, idx) => (
          <li key={idx} className="text-xs mb-2">
            <div className="flex items-center flex-wrap">
              <FilePathButton source={source} openDocumentModalWith={openDocumentModalWith} />
              <ScoreBadge source={source} theme={theme} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MessageBot = ({ msg, index, theme, copyToClipboard, copySuccess, handleReadAloud, playingMessageId, showSourcesForMessage, setShowSourcesForMessage, openDocumentModalWith }) => (
  <div className={`flex flex-col space-y-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} message-bot`}>
    {msg.status && (
      <div className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{msg.status}</span>
      </div>
    )}

    {msg.content && <ChatResponseDisplay answer={msg.content} sources={msg.sources} theme={theme} />}

    {msg.content && (
      <>
        <MessageActions
          msg={msg}
          index={index}
          theme={theme}
          copyToClipboard={copyToClipboard}
          copySuccess={copySuccess}
          handleReadAloud={handleReadAloud}
          playingMessageId={playingMessageId}
          showSourcesForMessage={showSourcesForMessage}
          setShowSourcesForMessage={setShowSourcesForMessage}
        />
        <MessageSources
          msg={msg}
          theme={theme}
          showSourcesForMessage={showSourcesForMessage}
          index={index}
          openDocumentModalWith={openDocumentModalWith}
        />
      </>
    )}
  </div>
);

const MessageList = () => {
  const { theme, messages, loading, error, openDocumentModalWith } = useAppContext();
  const { copyToClipboard, copySuccess, showSourcesForMessage, setShowSourcesForMessage, playingMessageId, handleReadAloud } = useChat();

  return (
    <div className="flex-grow p-6 overflow-y-auto scrollbar-hide messages-container">
      <div className="max-w-4xl mx-auto">
        {messages.length === 0 && !loading && <WelcomeScreen />}

        {messages.map((msg, index) => (
          <div key={index} className={`flex items-start gap-4 mb-8 ${index === 0 ? 'fade-in-up' : ''}`}>
            <div className="flex-1">
              {msg.role === 'user' ? (
                <p className={`text-right text-2xl font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'} message-user`}>
                  {msg.content}
                </p>
              ) : (
                <MessageBot
                  msg={msg}
                  index={index}
                  theme={theme}
                  copyToClipboard={copyToClipboard}
                  copySuccess={copySuccess}
                  handleReadAloud={handleReadAloud}
                  playingMessageId={playingMessageId}
                  showSourcesForMessage={showSourcesForMessage}
                  setShowSourcesForMessage={setShowSourcesForMessage}
                  openDocumentModalWith={openDocumentModalWith}
                />
              )}
            </div>
          </div>
        ))}

        {error && <div className="text-red-500 text-center fade-in-up">{error}</div>}
      </div>
    </div>
  );
};

export default MessageList;
